import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ButtonDS, PhotosInputDS, Spacer, TextCapitalized } from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { Badge } from "@qivia/ui/src/designSystem/components/Tag/Badge";
import { TextAreaDS } from "@qivia/ui/src/designSystem/components/Inputs/TextAreaInput";
import { MessageTooltip } from "@qivia/ui/src/designSystem/components/MessageTooltip";
import { TaskAltOutlined } from "@qivia/ui/src/designSystem/materialUi/materialUi";
import { Page } from "../../components/Page";
import "../../../i18n/i18n";

interface VehicleRestitutionSummaryStepProps {
  departureLocation: string;
  furthestDestination: string;
  mileage: string;
  isCabinCleaned: boolean;
  isTrunkCleaned: boolean;
  observations: string;
  photos: File[];
}

export const VehicleRestitutionSummaryStep = (
  props: VehicleRestitutionSummaryStepProps,
) => {
  const { t } = useTranslation();

  const detailRow = (label: string, value: string) => (
    <StyledFlex>
      <StyledDetailLabel>
        <TextCapitalized>{label}</TextCapitalized>
      </StyledDetailLabel>
      <Spacer x={1} />
      <StyledDetailValue>
        <TextCapitalized>{value}</TextCapitalized>
      </StyledDetailValue>
    </StyledFlex>
  );

  const stateBadge = (value: boolean) => (
    <Badge
      text={t(`restitution.summary.${value}`)}
      backgroundColor={
        value
          ? colors["colors/system/success/success_ultraLight"]
          : colors["colors/system/error/error_ultraLight"]
      }
      textColor={
        value
          ? colors["colors/system/success/success_normal"]
          : colors["colors/system/error/error_normal"]
      }
    />
  );

  return (
    <Page
      title={
        <StyledHeader>
          <StyledCheckIcon>
            <TaskAltOutlined />
          </StyledCheckIcon>
          <Spacer y={0.5} />
          <TextCapitalized>{t("restitution.summary.title")}</TextCapitalized>
        </StyledHeader>
      }
      withoutHeaderSpacer
      centerHeader
      helpButton
      bottomButtonBackgroundColor="white"
      withShadow
      bottomButton={
        <StyledButtons>
          <ButtonDS
            text={t("restitution.button.close")}
            buttonType="primary"
            format="fill"
            onClick={() => window.close()}
          />
          <Spacer y={0.5} />
          <ButtonDS
            text={t("restitution.button.edit")}
            buttonType="secondary"
            format="fill"
            onClick={() => console.log("edit restitution")}
          />
        </StyledButtons>
      }
    >
      <StyledForm>
        <Spacer y={1} />
        <StyledBanner>
          <MessageTooltip
            type="SUCCESS"
            title={t("restitution.summary.subtitle.title")}
            text={t("restitution.summary.subtitle.text")}
            iconLeft={<TaskAltOutlined />}
          />
        </StyledBanner>
        <Spacer y={1.5} />
        <StyledSeparator />
        <Spacer y={1.5} />
        <StyledSectionTitle>
          <TextCapitalized>{t("restitution.summary.details")}</TextCapitalized>
        </StyledSectionTitle>
        <Spacer y={1.5} />
        {detailRow(
          t("restitution.vehicleReturn.departureLocation.label"),
          props.departureLocation,
        )}
        <Spacer y={0.5} />
        {detailRow(
          t("restitution.vehicleReturn.furthestDestination.label"),
          props.furthestDestination,
        )}
        <Spacer y={0.5} />
        {detailRow(t("restitution.vehicleReturn.mileage.label"), props.mileage)}
        <Spacer y={1.5} />
        <StyledSeparator />
        <Spacer y={1.5} />
        <StyledSectionTitle>
          <TextCapitalized>
            {t("restitution.vehicleCondition.title")}
          </TextCapitalized>
        </StyledSectionTitle>
        <Spacer y={1.5} />
        <StyledRow>
          <StyledLabel>
            <TextCapitalized>
              {t("restitution.vehicleCondition.isCabinCleaned")}
            </TextCapitalized>
          </StyledLabel>
          {stateBadge(props.isCabinCleaned)}
        </StyledRow>
        <Spacer y={0.5} />
        <StyledRow>
          <StyledLabel>
            <TextCapitalized>
              {t("restitution.vehicleCondition.isTrunkCleaned")}
            </TextCapitalized>
          </StyledLabel>
          {stateBadge(props.isTrunkCleaned)}
        </StyledRow>
        <Spacer y={1} />
        <StyledLabel>
          <TextCapitalized>
            {t("restitution.vehicleCondition.observations.label")}
          </TextCapitalized>
        </StyledLabel>
        <Spacer y={0.5} />
        <StyledTextArea>
          <TextAreaDS
            value={props.observations}
            $update={() => undefined}
            isDisabled
          />
        </StyledTextArea>
        {props.photos.length > 0 && (
          <>
            <Spacer y={1} />
            <StyledLabel>
              <TextCapitalized>
                {t("restitution.vehicleCondition.photos.label")}
              </TextCapitalized>
            </StyledLabel>
            <Spacer y={0.5} />
            <PhotosInputDS photos={props.photos} />
          </>
        )}
      </StyledForm>
    </Page>
  );
};

const StyledButtons = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledCheckIcon = styled.div`
  display: flex;
  & svg {
    width: 3rem;
    height: 3rem;
    fill: ${colors["colors/system/success/success_normal"]};
  }
`;

const StyledSeparator = styled.div`
  width: 100%;
  border-bottom: 0.0625rem solid ${colors["colors/borders/separator/cells"]};
`;

const StyledSectionTitle = styled.div`
  display: flex;
  font-weight: 600;
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledBanner = styled.div`
  width: 100%;
  & > div {
    box-sizing: border-box;
  }
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 2rem;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledLabel = styled.div`
  ${typographies["Body/L"]}
`;

const StyledDetailLabel = styled.div`
  ${typographies["Body/L"]}
  width: 8rem;
  flex-shrink: 0;
`;

const StyledDetailValue = styled.div`
  flex: 1;
  text-align: right;
`;

const StyledTextArea = styled.div`
  width: 100%;
  & textarea {
    box-sizing: border-box;
  }
`;
